'use client';

import { createClient } from '@/lib/supabase/client';

export interface PipelineALeadData {
  fullName: string;
  email: string;
  phone?: string;
  company: string;
  sector: string;
  emissionsBaseline?: string;
  complianceDeadline?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface PipelineBLeadData {
  fullName: string;
  email: string;
  phone?: string;
  company: string;
  vendorType: string;
  targetMarket: string;
  brsrStatus?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface LeadMagnetDownloadData {
  email: string;
  fullName?: string;
  resourceName: string;
  resourceType: string;
  pipeline?: string;
  utmSource?: string;
  utmMedium?: string;
}

function getUTMParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
}

export async function submitPipelineALead(data: PipelineALeadData): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createClient();
    const utm = getUTMParams();

    const { error } = await supabase.from('pipeline_a_leads').insert({
      full_name: data.fullName,
      email: data.email,
      phone: data.phone || null,
      company: data.company,
      sector: data.sector,
      emissions_baseline: data.emissionsBaseline || null,
      compliance_deadline: data.complianceDeadline || null,
      pipeline: 'A',
      source: 'website',
      utm_source: data.utmSource || utm.utmSource || null,
      utm_medium: data.utmMedium || utm.utmMedium || null,
      utm_campaign: data.utmCampaign || utm.utmCampaign || null,
      status: 'new',
    });

    if (error) {
      console.error('Pipeline A lead submission error:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('Pipeline A lead service error:', err);
    return { success: false, error: err.message };
  }
}

export async function submitPipelineBLead(data: PipelineBLeadData): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createClient();
    const utm = getUTMParams();

    const { error } = await supabase.from('pipeline_b_leads').insert({
      full_name: data.fullName,
      email: data.email,
      phone: data.phone || null,
      company: data.company,
      vendor_type: data.vendorType,
      target_market: data.targetMarket,
      brsr_status: data.brsrStatus || null,
      pipeline: 'B',
      source: 'website',
      utm_source: data.utmSource || utm.utmSource || null,
      utm_medium: data.utmMedium || utm.utmMedium || null,
      utm_campaign: data.utmCampaign || utm.utmCampaign || null,
      status: 'new',
    });

    if (error) {
      console.error('Pipeline B lead submission error:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('Pipeline B lead service error:', err);
    return { success: false, error: err.message };
  }
}

export async function submitLeadMagnetDownload(data: LeadMagnetDownloadData): Promise<{ success: boolean; error?: string }> {
  try {
    const supabase = createClient();
    const utm = getUTMParams();

    const { error } = await supabase.from('lead_magnet_downloads').insert({
      email: data.email,
      full_name: data.fullName || null,
      resource_name: data.resourceName,
      resource_type: data.resourceType,
      pipeline: data.pipeline || null,
      source: 'website',
      utm_source: data.utmSource || utm.utmSource || null,
      utm_medium: data.utmMedium || utm.utmMedium || null,
    });

    if (error) {
      console.error('Lead magnet download error:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('Lead magnet service error:', err);
    return { success: false, error: err.message };
  }
}
